import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase, resolveUserRole, type AccountRole } from '@/lib/supabase';

const REMEMBER_KEY = 'daba_remember_session';

type AuthState = {
  session: Session | null;
  role: AccountRole | null;
  /** True until the initial session + role lookup settles. */
  loading: boolean;
  banned: boolean;
  signOut: () => Promise<void>;
  refreshRole: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AccountRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [banned, setBanned] = useState(false);
  const loginRecorded = useRef(false);

  useEffect(() => {
    let active = true;

    const applySession = async (next: Session | null) => {
      if (!active) return;

      if (!next) {
        setSession(null);
        setRole(null);
        setBanned(false);
        setLoading(false);
        loginRecorded.current = false;
        return;
      }

      const nextRole = await resolveUserRole(next.user.id);
      if (!active) return;

      if (nextRole === null) {
        // Banned accounts are signed out immediately.
        setBanned(true);
        await supabase.auth.signOut().catch(() => {});
        setSession(null);
        setRole(null);
        setLoading(false);
        return;
      }

      setBanned(false);
      setSession(next);
      setRole(nextRole);
      setLoading(false);

      if (!loginRecorded.current) {
        loginRecorded.current = true;
        void supabase.rpc('record_login');
      }
    };

    void (async () => {
      const { data } = await supabase.auth.getSession();

      // "Rester connecté" was declined on the last login: drop the session.
      if (data.session && window.localStorage.getItem(REMEMBER_KEY) === 'false') {
        await supabase.auth.signOut().catch(() => {});
        window.localStorage.removeItem(REMEMBER_KEY);
        void applySession(null);
        return;
      }

      void applySession(data.session);
    })();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      void applySession(nextSession);
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      role,
      loading,
      banned,
      signOut: async () => {
        window.localStorage.removeItem(REMEMBER_KEY);
        await supabase.auth.signOut();
      },
      refreshRole: async () => {
        if (!session) return;
        const nextRole = await resolveUserRole(session.user.id);
        setRole(nextRole);
      },
    }),
    [session, role, loading, banned]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

/** Current user id, or an empty string while the session is still loading. */
export function useUserId(): string {
  const { session } = useAuth();
  return session?.user.id ?? '';
}

export const rememberSession = (remember: boolean) =>
  window.localStorage.setItem(REMEMBER_KEY, remember ? 'true' : 'false');
